// ***** src/pages/PartitionDetail.js *****
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal, Button, Container, Row, Col, Table } from 'react-bootstrap';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { FaHome, FaServer, FaList, FaUser, FaTrophy } from 'react-icons/fa';
import deviceCover from '../assets/deviceCover.png';
import { motion, AnimatePresence } from 'framer-motion';
import HistoryModal from '../components/HistoryModal';
import ProfileModal from '../components/ProfileModal';
import AnnouncementBoard from '../components/AnnouncementBoard';
import MiniHistoryGrid from '../components/MiniHistoryGrid';

function PartitionDetail() {
  const { partitionId } = useParams();
  const navigate        = useNavigate();
  const { user, socket } = useAuth();

  /* -------- 所有 State -------- */
  const [partitionName, setPartitionName] = useState('');
  const [devices, setDevices]             = useState([]);
  const [loading, setLoading]             = useState(true);

  const [showMyDevicesModal, setShowMyDevicesModal] = useState(false);
  const [myDevices, setMyDevices]                   = useState([]);
  const [myDevicesLoading, setMyDevicesLoading]     = useState(false);

  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs]                   = useState([]);
  const [logsLoading, setLogsLoading]     = useState(false);
  const [logsPage, setLogsPage]           = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const logsLimit = 10;  const [showHistModal, setShowHistModal] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // 排行榜相关状态
  const [showRankingsModal, setShowRankingsModal] = useState(false);
  const [rankingsData, setRankingsData] = useState({
    burstRankings: [],
    resistanceRankings: [],
    totalDevices: 0,
    activeDevices: 0
  });
  const [rankingsLoading, setRankingsLoading] = useState(false);

  /* ---------- 工具函数 ---------- */
  function parseLeadingNumber(name) {
    const m = name?.match(/^(\d+)/);
    return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
  }

  const handleDeviceCardClick = (dev) => {
    // 维护中设备不可进入
    if (dev.status === '维修') return alert('设备维修中，无法查看详情');
    if (!dev.online) return alert('设备离线，无法查看详情');
    const userId = user?._id || user?.id;
    const holderId = typeof dev.currentHolder === 'object' && dev.currentHolder?._id ? dev.currentHolder._id : dev.currentHolder;
    const secondaryId = typeof dev.secondaryHolder === 'object' && dev.secondaryHolder?._id ? dev.secondaryHolder._id : dev.secondaryHolder;
    const isSanqi = (dev.type === 'sanqi') || (dev.modelType === 'sanqi') || (dev.modelType === '三七机');
    if (dev.status === 'occupied' && holderId !== userId) {
      // 三七机不支持双打：被占用时除持有人外不允许进入
      if (isSanqi) {
        return alert('该设备（三七机）正被他人占用，无法进入');
      }
      // 钢珠机：允许未满时进入详情尝试加入双打
      if (!secondaryId || secondaryId === userId) {
        navigate(`/devices/${dev._id}`);
        return;
      }
      return alert('设备正被他人占用，无法进入 (双打已满)');
    }
    navigate(`/devices/${dev._id}`);
  };
  const handleShowHistory = (dev, e) => {
    e.stopPropagation();
    setSelectedDeviceId(dev._id);
    setShowHistModal(true);
  };

  // 判断是否为移动端（仅在渲染时用，不影响 SSR）
  const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  /* ---------- 1) 拉取分区设备 ---------- */
  function isValidObjectId(id) {
    return typeof id === 'string' && /^[a-fA-F0-9]{24}$/.test(id);
  }
  // 拉取分区设备（只刷新卡片数据）
  const firstLoadRef = useRef(true);
  const fetchPartitionDevices = async () => {
    if (!isValidObjectId(partitionId)) {
      setPartitionName('分区详情');
      setDevices([]);
      setLoading(false);
      return;
    }
    if (firstLoadRef.current) {
      setLoading(true);
    }
    try {
      const res = await api.get(`/api/partitions/${partitionId}/devices`);
      let list = res.data.devices || [];
      list.sort((a, b) => parseLeadingNumber(a.name) - parseLeadingNumber(b.name));      setDevices(prev => {
        const prevMap = Object.fromEntries(prev.map(d => [d._id, d]));
        const updatedList = list.map(dev => {
          const old = prevMap[dev._id];
          // remainSec 只在 occupied 且状态未变时递减，否则用后端新值
          if (old && typeof old.remainSec === 'number' && old.remainSec > 0 && dev.status === old.status && dev.status === 'occupied') {
            return { ...dev, remainSec: old.remainSec };
          }
          // 如果设备已空闲或非 occupied，remainSec 立即归零
          if (dev.status !== 'occupied') {
            return { ...dev, remainSec: 0 };
          }
          return dev;
        });
        return updatedList;
      });
      setPartitionName(res.data.partition?.name || '分区详情');
    } catch (err) {
      // 定时刷新失败时不清空卡片、不全局 loading，只可选提示
      // 可选：console.error(err)
    } finally {
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
    }
  };
  // 页面加载时拉取一次
  useEffect(() => {
    fetchPartitionDevices();
  }, [partitionId]);
  // 定时只刷新卡片数据
  useEffect(() => {
    const timer = setInterval(fetchPartitionDevices, 5000);
    return () => clearInterval(timer);
  }, [partitionId]);  // 本地倒计时更新，每秒递减 remainSec，不再依赖接口轮询
  useEffect(() => {
    let refreshTimeout = null;
    const timer = setInterval(() => {
      setDevices(prev => {
        let needRefresh = false;
        const updated = prev.map(dev => {
          if (dev.online && dev.status === 'occupied' && typeof dev.remainSec === 'number' && dev.remainSec > 0) {
            const nextSec = dev.remainSec - 1;
            if (nextSec <= 0) needRefresh = true;
            return { ...dev, remainSec: nextSec };
          }
          return dev;
        });
        // remainSec 归零时，自动触发一次设备状态刷新（防抖，避免多次触发）
        if (needRefresh && !refreshTimeout) {
          refreshTimeout = setTimeout(() => {
            fetchPartitionDevices();
            refreshTimeout = null;
          }, 300); // 300ms 防抖
        }
        return updated;
      });
    }, 1000);
    return () => {
      clearInterval(timer);
      if (refreshTimeout) clearTimeout(refreshTimeout);
    };
  }, []);

  // Socket.IO 监听设备状态实时更新
  useEffect(() => {
    if (!socket) return;    const handleDeviceStatusUpdate = (data) => {
      console.log('[PartitionDetail] 收到设备状态更新:', data);
      const { deviceId, status, currentHolder, tcpDeviceId } = data;
      
      // 更新对应设备的状态 - 支持两种ID匹配方式
      setDevices(prev => prev.map(dev => {
        if (dev._id === deviceId || dev.deviceId === tcpDeviceId) {
          console.log(`[PartitionDetail] 更新设备状态: ${dev.name} -> ${status}`);
          return {
            ...dev,
            status: status === '可用' || status === 'idle' ? 'idle' : status, // 统一状态格式
            currentHolder: currentHolder,
            remainSec: (status === 'idle' || status === '可用') ? 0 : dev.remainSec // 如果变为空闲，立即清零剩余时间
          };
        }
        return dev;
      }));
    };// 监听设备状态更新事件
    socket.on('deviceStatusUpdate', handleDeviceStatusUpdate);

    return () => {
      socket.off('deviceStatusUpdate', handleDeviceStatusUpdate);
    };
  }, [socket]);

  // 备用机制：定期检查和同步设备状态（用于兜底）
  useEffect(() => {
    const statusSyncInterval = setInterval(() => {
      // 检查是否有设备显示为"游戏中"但剩余时间为0，如果有则刷新数据
      const needsRefresh = devices.some(dev => 
        dev.status === 'occupied' && 
        typeof dev.remainSec === 'number' && 
        dev.remainSec <= 0
      );
      
      if (needsRefresh) {
        console.log('[PartitionDetail] 检测到设备状态可能不同步，触发刷新');
        fetchPartitionDevices();
      }
    }, 30000); // 每30秒检查一次

    return () => clearInterval(statusSyncInterval);
  }, [devices]);

  /* ---------- 2) 我的设备弹窗 ---------- */
  const handleShowMyDevices = async () => {
    setShowMyDevicesModal(true);
    setMyDevicesLoading(true);
    try {
      const res = await api.get('/api/users/my-devices');
      setMyDevices(res.data.devices || []);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || '获取我的设备失败');
    } finally {
      setMyDevicesLoading(false);
    }
  };
  const handleReleaseDevice = async (device) => {
    if (!window.confirm(`确定释放【${device.name}】?`)) return;
    try {
      // 先发送停止指令
      await api.post(`/api/devices/${device._id}/command`, { command: '###ks' });
      // 再发送下分指令
      await api.post(`/api/devices/${device._id}/command`, { command: '###zm' });
      // 最后释放设备
      await api.post(`/api/devices/${device._id}/release`);
      // 本地更新列表，移除已释放的设备
      setMyDevices(prev => prev.filter(d => d._id !== device._id));
      // 更新主页面设备状态为空闲
      setDevices(prev => prev.map(d => d._id === device._id ? { ...d, status: 'idle', currentHolder: null } : d));
      alert('设备已释放');
    } catch (err) {
      const msg = err.response?.data?.message || '释放失败';
      if (msg.includes('留机保护期')) {
        alert(msg);
      } else {
        alert(msg);
      }
    }
  };
  /* ---------- 3) 游戏明细弹窗 ---------- */
  const fetchLogs = async (page = 1) => {
    if (!user?._id) return alert('未登录');
    setLogsLoading(true);
    try {
      const res = await api.get(`/api/users/${user._id}/round-logs?page=${page}&limit=${logsLimit}`);
      setLogs(res.data.logs || []);
      setLogsPage(res.data.currentPage || 1);
      setLogsTotalPages(res.data.totalPages || 1);
    } catch (err) {
      alert(err.response?.data?.message || '获取账目失败');
    } finally {
      setLogsLoading(false);
    }
  };

  /* ---------- 排行榜相关函数 ---------- */
  const fetchRankings = async () => {
    if (!partitionId || !isValidObjectId(partitionId)) return;
    setRankingsLoading(true);
    try {
      const res = await api.get(`/api/devices/partition/${partitionId}/rankings`);
      if (res.data.success) {
        setRankingsData(res.data.data);
      }
    } catch (err) {
      console.error('获取排行榜失败:', err);
      alert(err.response?.data?.message || '获取排行榜数据失败');
    } finally {
      setRankingsLoading(false);
    }
  };

  const handleShowRankings = () => {
    setShowRankingsModal(true);
    fetchRankings();
  };

  /* ---------- 4) 渲染设备卡片 ---------- */
  const renderDeviceCard = (dev, idx) => {
    const coverUrl = dev.coverImageUrl
      ? `${process.env.REACT_APP_API_BASE_URL}${dev.coverImageUrl}` : deviceCover;

    /* ===== 设备状态判断 ===== */
    // 默认空闲
    let statusText  = '空闲';
    let statusColor = '#28c76f'; // 绿色
    let statusGradient = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
    
    // 维护状态
    if (dev.status === '维修') {
      statusText  = '维修中';
      statusColor = '#6b7280';
      statusGradient = 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
    }
    // 离线、开奖中、占用等状态
    if (!dev.online) {                           // 离线
      statusText  = '离线';
      statusColor = '#6b7280';
      statusGradient = 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
    } else if (dev.isInJackpot) {                // ★★ 开奖中
      statusText  = '大当たり';
      statusColor = '#f59e0b';
      statusGradient = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
    } else if (dev.status === 'occupied') {      // 游戏中
      statusText  = '游戏中';
      statusColor = '#ef4444';
      statusGradient = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
    }

    // 移动端专用高端动画效果
    const motionProps = isMobile ? {
      initial: { opacity: 0, y: 20, scale: 0.95 },
      animate: { 
        opacity: 1, 
        y: 0, 
        scale: 1,
        transition: {
          type: 'spring',
          stiffness: 260,
          damping: 20,
          delay: idx * 0.08,
          duration: 0.6
        }
      },
      exit: { opacity: 0, y: -20, scale: 0.95 },
      whileTap: { 
        scale: 0.96,
        transition: { duration: 0.1 }
      },
      style: { willChange: 'transform, opacity' }
    } : {
      initial: { opacity: 0, y: 48, scale: 0.96 },
      animate: { opacity: 1, y: 0, scale: 1 },
      exit: { opacity: 0, y: 48, scale: 0.96 },
      transition: { type: 'spring', stiffness: 180, damping: 22, delay: idx * 0.07 },
      whileHover: { scale: 1.02, y: -4 },
      whileTap: { scale: 0.98 },
      style: { willChange: 'transform, opacity' }
    };

    return (
      <motion.div
        key={dev._id}
        className="device-card-pachinko"
        {...motionProps}
      >
        {/* 弹珠机风格华丽卡片 */}
        <div style={styles.pachinkoCard} className="pachinko-card-container" onClick={() => handleDeviceCardClick(dev)}>
          {/* 顶部黄金标题区域 - 显示设备名称和履历按钮 */}
          <div style={styles.pachinkoHeader}>
            <div style={styles.pachinkoTitle}>
              <span style={styles.pachinkoTitleText}>{dev.name}</span>
            </div>
            {/* 履历按钮 */}
            <Button
              variant="light"
              size="sm"
              style={styles.pachinkoHistBtnInHeader}
              onClick={e => { e.stopPropagation(); handleShowHistory(dev, e); }}
              title="查看履历"
            >
              📊 履历
            </Button>
          </div>

          {/* 设备封面图片区域 */}
          <div style={styles.pachinkoCoverArea}>
            <img src={coverUrl} alt="设备封面" style={styles.pachinkoCoverImage}/>
            {/* 剩余时间显示 */}
            <div style={styles.pachinkoRemainTime}>
              {dev.online ? `${dev.remainSec || 0}s` : '--'}
            </div>
          </div>

          {/* 统计信息区域 - 显示真实设备数据 */}
          <div style={styles.pachinkoStatsArea}>
            <div style={styles.pachinkoStatsTitle}>设备信息</div>
            <div style={styles.pachinkoStatsGrid}>
              <div style={styles.pachinkoStatItem}>
                <span style={styles.pachinkoStatLabel}>当前转数</span>
                <span style={styles.pachinkoStatValue}>{(dev.turnCount || 0).toLocaleString()} 转</span>
              </div>
              <div style={styles.pachinkoStatItem}>
                <span style={styles.pachinkoStatLabel}>下分比例</span>
                <span style={styles.pachinkoStatValue}>{dev.fsFactor || 0}</span>
              </div>
              <div style={styles.pachinkoStatItem}>
                <span style={styles.pachinkoStatLabel}>每转分数</span>
                <span style={styles.pachinkoStatValue}>{dev.turnCost || 2.8} 分</span>
              </div>
              <div style={styles.pachinkoStatItem}>
                <span style={styles.pachinkoStatLabel}>开奖损耗</span>
                <span style={styles.pachinkoStatValue}>{dev.lossPercentage || 0}%</span>
              </div>
            </div>
            {/* 主列表卡片中的双打模式入口 —— 三七机不显示双打 */}
            {dev.type !== 'sanqi' && dev.modelType !== 'sanqi' && dev.modelType !== '三七机' && dev.currentHolder && !dev.secondaryHolder && dev.currentHolder !== user?._id && (
              <div style={styles.doublePlayEnterWrapper}>
                <Button
                  size="sm"
                  variant="warning"
                  style={styles.doublePlayEnterBtn}
                  onClick={(e) => { e.stopPropagation(); navigate(`/devices/${dev._id}`); }}
                  title="加入双打模式"
                >
                  双打模式
                </Button>
              </div>
            )}
          </div>

          {/* 迷你格子图履历区域 */}
          <div style={styles.pachinkoMiniHistoryArea}>
            <MiniHistoryGrid deviceId={dev._id} maxColumns={6} />
          </div>

          {/* 底部状态显示区域 - 简洁文字居中 */}
          <div style={styles.pachinkoBottomStatus}>
            <span style={styles.pachinkoStatusText}>{statusText}</span>
          </div>

          {/* 装饰性光效 */}
          <div style={styles.pachinkoGlowEffect}></div>
          <div style={styles.pachinkoSparkles}>
            <div style={styles.pachinkoSparkle1}>✨</div>
            <div style={styles.pachinkoSparkle2}>⭐</div>
            <div style={styles.pachinkoSparkle3}>✨</div>
          </div>
        </div>
      </motion.div>
    );
  };
  /* ---------- 5) 渲染 ---------- */  return (
    <div style={styles.pageWrapper}>
      <div style={styles.innerContainer}>
        <div style={styles.headerContainer}>
          <h1 style={styles.pageTitle}>{partitionName || '设备详情'}</h1>
          <Button 
            variant="outline-success" 
            onClick={handleShowRankings}
            style={styles.rankingBtn}
            className="ranking-header-btn"
          >
            <FaTrophy style={{marginRight: 6}} />
            排行榜
          </Button>
        </div>

        {/* 公告板组件 */}
        <div style={{ marginBottom: '20px' }}>
          <AnnouncementBoard />
        </div>

        {loading
          ? <p style={{textAlign:'center'}}>加载中...</p>
          : devices.length===0
            ? <p style={{textAlign:'center'}}>暂无设备</p>
            : (              /* 移动端优化的网格布局 */
              <div className="mobile-grid-optimized">
                <AnimatePresence>
                  {devices.map((dev, idx) => renderDeviceCard(dev, idx))}
                </AnimatePresence>
              </div>
            )}
      </div>      {/* 底部操作栏 */}
      <div style={styles.footerBar}>
        <Container fluid>
          <Row className="w-100 m-0">
            <Col xs={3} className="footer-btn-premium-col" style={styles.footerBtnCol}>
              <Button 
                variant="outline-warning" 
                onClick={()=>navigate('/lobby')} 
                className="footer-btn-premium footer-btn-home"
                style={styles.footerBtn}
              >
                <FaHome style={{marginRight:4}}/> 
                <span className="btn-text">大厅</span>
              </Button>
            </Col>
            <Col xs={3} className="footer-btn-premium-col" style={styles.footerBtnCol}>
              <Button 
                variant="outline-danger" 
                onClick={handleShowMyDevices} 
                className="footer-btn-premium footer-btn-devices"
                style={styles.footerBtn}
              >
                <FaServer style={{marginRight:4}}/> 
                <span className="btn-text">设备</span>
              </Button>
            </Col>
            <Col xs={3} className="footer-btn-premium-col" style={styles.footerBtnCol}>
              <Button 
                variant="outline-info" 
                onClick={()=>{setShowLogsModal(true); fetchLogs(1);}} 
                className="footer-btn-premium footer-btn-logs"
                style={styles.footerBtn}
              >
                <FaList style={{marginRight:4}}/> 
                <span className="btn-text">明细</span>
              </Button>
            </Col>
            <Col xs={3} className="footer-btn-premium-col" style={styles.footerBtnCol}>
              <Button 
                variant="outline-primary" 
                onClick={() => setShowProfileModal(true)} 
                className="footer-btn-premium footer-btn-profile"
                style={styles.footerBtn}
              >
                <FaUser style={{marginRight:4}}/> 
                <span className="btn-text">信息</span>
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      {/* ---------- (A) 我的设备弹窗 ---------- */}
      <Modal show={showMyDevicesModal} onHide={()=>setShowMyDevicesModal(false)}>
        <Modal.Header closeButton><Modal.Title>我占用的设备</Modal.Title></Modal.Header>
        <Modal.Body>
          {myDevicesLoading
            ? <p>加载中...</p>
            : myDevices.length===0
              ? <p>当前未占用任何设备</p>
              : (
                <ul style={{listStyle:'none',paddingLeft:0,marginBottom:0}}>
                  {myDevices.map(dev=>(
                    <li key={dev._id} style={{padding:'8px 0',borderBottom:'1px solid #ccc'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <span style={{cursor:'pointer'}} onClick={()=>{setShowMyDevicesModal(false); navigate(`/devices/${dev._id}`);}}>
                          <strong>{dev.name}</strong> - {dev.description||'无简介'}
                        </span>
                        <Button variant="outline-danger" size="sm" onClick={()=>handleReleaseDevice(dev)}>释放</Button>
                      </div>
                      {/* 双打模式入口按钮：存在主玩家且无副玩家 且 不是自己占为副玩家时显示 */}
                      {dev.currentHolder && !dev.secondaryHolder && dev.currentHolder !== user?._id && (
                        <div style={styles.doublePlayEnterWrapper}>
                          <Button
                            size="sm"
                            variant="warning"
                            style={styles.doublePlayEnterBtn}
                            onClick={(e) => { e.stopPropagation(); navigate(`/devices/${dev._id}`); }}
                            title="加入双打模式"
                          >
                            双打模式
                          </Button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
        </Modal.Body>
      </Modal>      {/* ---------- (B) 游戏明细弹窗 ---------- */}
      <Modal show={showLogsModal} onHide={()=>setShowLogsModal(false)} size="lg" dialogClassName="game-detail-modal-responsive">
        <Modal.Header closeButton>
          <Modal.Title className="game-detail-title">账目明细</Modal.Title>
        </Modal.Header>
        <Modal.Body className="game-detail-body">
          {logsLoading
            ? <div className="loading-container"><p>加载中...</p></div>
            : logs.length===0
              ? <div className="empty-container"><p>暂无账目记录</p></div>
              : (
                <>
                  {/* 总输赢统计 */}
                  <div className="summary-stats">
                    总输赢：<span className="summary-amount">{logs.reduce((sum, r) => sum + (r.netProfit || 0), 0).toFixed(2)}</span>
                  </div>
                  
                  {/* 移动端卡片式显示 */}
                  <div className="mobile-card-list d-block d-md-none">
                    {logs.map(r => (
                      <div key={r._id} className="game-record-card">
                        <div className="card-header">
                          <span className="round-id">#{r._id.slice(-4)}</span>
                          <span className={`profit ${r.netProfit >= 0 ? 'positive' : 'negative'}`}>
                            {r.netProfit >= 0 ? '+' : ''}{r.netProfit?.toFixed(1)}
                          </span>
                        </div>
                        <div className="card-content">
                          <div className="info-row">
                            <span className="label">设备:</span>
                            <span className="value">{r.deviceName || '未知设备'}</span>
                          </div>
                          <div className="info-row">
                            <span className="label">投注:</span>
                            <span className="value">{r.betTotal?.toFixed(1)}</span>
                            <span className="label">返还:</span>
                            <span className="value">{r.payoutTotal?.toFixed(1)}</span>
                          </div>
                          <div className="info-row">
                            <span className="label">开始:</span>
                            <span className="value small">{new Date(r.startTime).toLocaleString()}</span>
                          </div>
                          {r.endTime && (
                            <div className="info-row">
                              <span className="label">结束:</span>
                              <span className="value small">{new Date(r.endTime).toLocaleString()}</span>
                            </div>
                          )}
                          {r.reason && (
                            <div className="info-row">
                              <span className="label">原因:</span>
                              <span className="value">{r.reason}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* 桌面端表格显示 */}
                  <div className="desktop-table d-none d-md-block">
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover">
                        <thead>
                          <tr>
                            <th>轮次ID</th>
                            <th>设备</th>
                            <th>开始</th>
                            <th>结束</th>
                            <th>投注</th>
                            <th>返还</th>
                            <th>净盈亏</th>
                            <th>原因</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map(r => (
                            <tr key={r._id}>
                              <td>{r._id.slice(-4)}</td>
                              <td>{r.deviceName || '未知设备'}</td>
                              <td>{new Date(r.startTime).toLocaleString()}</td>
                              <td>{r.endTime ? new Date(r.endTime).toLocaleString() : '—'}</td>
                              <td>{r.betTotal?.toFixed(1)}</td>
                              <td>{r.payoutTotal?.toFixed(1)}</td>
                              <td style={{ color: r.netProfit >= 0 ? 'green' : 'red', fontWeight:700 }}>{r.netProfit?.toFixed(1)}</td>
                              <td>{r.reason || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  
                  {/* 分页控制 */}
                  <div className="pagination-controls">
                    <Button 
                      size="sm" 
                      disabled={logsPage<=1} 
                      onClick={()=>fetchLogs(logsPage-1)}
                      className="page-btn"
                    >
                      上一页
                    </Button>
                    <span className="page-info">第 {logsPage}/{logsTotalPages} 页</span>
                    <Button 
                      size="sm" 
                      disabled={logsPage>=logsTotalPages} 
                      onClick={()=>fetchLogs(logsPage+1)}
                      className="page-btn"
                    >
                      下一页
                    </Button>
                  </div>
                </>
              )}
        </Modal.Body>
        <Modal.Footer className="game-detail-footer">
          <Button variant="secondary" onClick={()=>setShowLogsModal(false)} className="close-btn">
            关闭
          </Button>
        </Modal.Footer>
      </Modal>      {/* ---------- (C) 履历弹窗 ---------- */}
      <HistoryModal 
        show={showHistModal} 
        onHide={() => setShowHistModal(false)}
        deviceId={selectedDeviceId}
      />
      
      {/* ---------- (D) 个人信息弹窗 ---------- */}
      <ProfileModal 
        show={showProfileModal} 
        onHide={() => setShowProfileModal(false)} 
      />      {/* ---------- (E) 排行榜弹窗 ---------- */}
      <Modal 
        show={showRankingsModal} 
        onHide={() => setShowRankingsModal(false)}
        size="lg"
        className="rankings-modal dark-theme"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            🏆 {rankingsData.partitionName || partitionName} - 设备排行榜
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rankingsLoading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">加载中...</span>
              </div>
              <p className="mt-2">正在获取排行榜数据...</p>
            </div>
          ) : (
            <>
              {/* 分区统计信息 */}
              <div className="mb-4 p-3 bg-light rounded">
                <Row>
                  <Col xs={6}>
                    <div className="text-center">
                      <h5 className="mb-1 text-primary">{rankingsData.totalDevices}</h5>
                      <small className="text-muted">总设备数</small>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="text-center">
                      <h5 className="mb-1 text-success">{rankingsData.activeDevices}</h5>
                      <small className="text-muted">活跃设备</small>
                    </div>
                  </Col>
                </Row>
              </div>              {/* 爆发排行榜 */}
              <div className="mb-4">
                <h5 className="mb-3 ranking-section-title burst-title">
                  💥 爆发排行榜 <small className="ranking-subtitle">(历史最高连奖次数)</small>
                </h5>
                {rankingsData.burstRankings.length > 0 ? (
                  <div className="ranking-list">
                    {rankingsData.burstRankings.map((item, index) => (
                      <div key={item.deviceId} className={`ranking-item rank-${item.rank}`}>
                        <div className="rank-badge">
                          {item.rank === 1 ? '🥇' : item.rank === 2 ? '🥈' : '🥉'}
                          {item.rank}
                        </div>
                        <div className="device-info">
                          <div className="device-name">{item.deviceName}</div>
                          <div className="device-number">设备号: {item.deviceNumber}</div>
                        </div>
                        <div className="stats">
                          <div className="primary-stat">
                            <span className="value">{item.consecutivePrize}</span>
                            <span className="label">连奖</span>
                          </div>
                          <div className="secondary-stat">
                            <span className="value">{item.currentTurns}</span>
                            <span className="label">转数</span>
                          </div>
                          <div className={`status-badge ${item.isInJackpot ? 'jackpot' : item.status === 'occupied' ? 'occupied' : 'idle'}`}>
                            {item.isInJackpot ? '开奖中' : item.status === 'occupied' ? '游戏中' : '空闲'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted p-3">
                    <p>暂无连奖设备</p>
                  </div>
                )}
              </div>              {/* 抗井排行榜 */}
              <div>
                <h5 className="mb-3 ranking-section-title resistance-title">
                  🛡️ 抗井排行榜 <small className="ranking-subtitle">(历史最高开奖转数)</small>
                </h5>
                {rankingsData.resistanceRankings.length > 0 ? (
                  <div className="ranking-list">
                    {rankingsData.resistanceRankings.map((item, index) => (
                      <div key={item.deviceId} className={`ranking-item rank-${item.rank}`}>
                        <div className="rank-badge">
                          {item.rank === 1 ? '🥇' : item.rank === 2 ? '🥈' : '🥉'}
                          {item.rank}
                        </div>
                        <div className="device-info">
                          <div className="device-name">{item.deviceName}</div>
                          <div className="device-number">设备号: {item.deviceNumber}</div>
                        </div>                        <div className="stats">
                          <div className="primary-stat">
                            <span className="value">{item.maxTurns}</span>
                            <span className="label">最高转数</span>
                          </div>
                        </div>                        <div className={`status-badge ${item.status === 'occupied' ? 'occupied' : 'idle'}`}>
                          {item.status === 'occupied' ? '游戏中' : '空闲'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-muted p-3">
                    <p>暂无历史开奖数据</p>
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRankingsModal(false)}>
            关闭
          </Button>
          <Button variant="primary" onClick={fetchRankings} disabled={rankingsLoading}>
            刷新数据
          </Button>
        </Modal.Footer>
      </Modal>
      <style>{`
        /* ====== 游戏明细弹窗移动端优化 ====== */
        .game-detail-modal-responsive .modal-dialog {
          max-width: 95vw;
          margin: 10px auto;
        }
        
        .game-detail-modal-responsive .modal-content {
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
        }
        
        .game-detail-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1a365d;
        }
        
        .game-detail-body {
          padding: 16px;
          max-height: 70vh;
          overflow-y: auto;
        }
        
        .loading-container, .empty-container {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }
        
        .summary-stats {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          text-align: center;
          font-weight: 600;
          font-size: 1rem;
        }
        
        .summary-amount {
          font-size: 1.2rem;
          font-weight: bold;
          margin-left: 8px;
        }
        
        /* 移动端卡片式显示 */
        .mobile-card-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .game-record-card {
          background: #fff;
          border-radius: 10px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }
        
        .card-header {
          background: linear-gradient(90deg, #f8fafc 0%, #e2e8f0 100%);
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .round-id {
          font-weight: 600;
          color: #4a5568;
          font-size: 0.9rem;
        }
        
        .profit {
          font-weight: bold;
          font-size: 1rem;
          padding: 4px 8px;
          border-radius: 6px;
        }
        
        .profit.positive {
          color: #22c55e;
          background: rgba(34, 197, 94, 0.1);
        }
        
        .profit.negative {
          color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
        }
        
        .card-content {
          padding: 12px 16px;
        }
        
        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .info-row:last-child {
          margin-bottom: 0;
        }
        
        .label {
          font-weight: 500;
          color: #64748b;
          font-size: 0.85rem;
          min-width: 40px;
        }
        
        .value {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.85rem;
          text-align: right;
        }
        
        .value.small {
          font-size: 0.75rem;
          color: #64748b;
        }
        
        /* 分页控制 */
        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 16px;
          padding: 12px 0;
          border-top: 1px solid #e2e8f0;
        }
        
        .page-btn {
          min-width: 70px;
          border-radius: 6px;
          font-weight: 500;
        }
        
        .page-info {
          font-weight: 500;
          color: #64748b;
          font-size: 0.9rem;
        }
        
        .game-detail-footer {
          padding: 12px 16px;
          border-top: 1px solid #e2e8f0;
        }
        
        .close-btn {
          width: 100%;
          border-radius: 8px;
          font-weight: 500;
        }
        
        /* 移动端响应式调整 */
        @media (max-width: 768px) {
          .game-detail-modal-responsive .modal-dialog {
            max-width: 100vw;
            width: 100vw;
            margin: 0;
            height: 100vh;
          }
          
          .game-detail-modal-responsive .modal-content {
            border-radius: 0;
            height: 100vh;
            display: flex;
            flex-direction: column;
          }
          
          .game-detail-body {
            flex: 1;
            overflow-y: auto;
            padding: 12px;
            max-height: none;
          }
          
          .summary-stats {
            margin-bottom: 12px;
            padding: 10px 12px;
            font-size: 0.9rem;
          }
          
          .summary-amount {
            font-size: 1.1rem;
          }
          
          .game-record-card {
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }
          
          .card-header {
            padding: 10px 12px;
          }
          
          .card-content {
            padding: 10px 12px;
          }
          
          .info-row {
            margin-bottom: 6px;
          }
          
          .pagination-controls {
            margin-top: 12px;
            padding: 10px 0;
          }
          
          .page-btn {
            min-width: 60px;
            font-size: 0.85rem;
          }
          
          .page-info {
            font-size: 0.85rem;
          }
        }
        
        @media (max-width: 480px) {
          .info-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
          }
          
          .value {
            text-align: left;
          }
          
          .game-detail-title {
            font-size: 1rem;
          }
        }
        
        /* 原有样式保持不变 */
        @media (max-width: 600px) {
          .lobby-modal-responsive .modal-dialog {
            max-width: 100vw !important;
            width: 100vw !important;
            margin: 0 !important;
          }
          .lobby-modal-responsive .modal-content {
            border-radius: 0;
            width: 100vw !important;
            min-width: 0 !important;
            padding: 0;
          }
          .lobby-modal-responsive .modal-body {
            padding: 8px 2px;
            overflow-x: auto;
          }
          .lobby-modal-responsive .table-responsive {
            width: 100%;
            overflow-x: auto;
          }
          .lobby-modal-responsive table {
            font-size: 13px;
            min-width: 540px;
            width: max-content;
            background: #fff;
          }
        }

        /* ====== 移动端优化的网格布局 ====== */
        .mobile-grid-optimized {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          padding: 0 4px;
        }
        
        @media (max-width: 600px) {
          .mobile-grid-optimized {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            padding: 0 2px;
          }
        }        /* ====== 高端发光边框卡片设计 (优化版本) ====== */        .device-card-minimal {
          position: relative;
          border-radius: 16px;
          overflow: visible;
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06),
            0 0 10px rgba(255, 255, 255, 0.05);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          animation: glow-pulse 4s ease-in-out infinite;
        }@keyframes glow-pulse {
          0% {
            border-color: rgba(255, 255, 255, 0.08);
            box-shadow: 
              0 4px 6px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06),
              0 0 20px rgba(255, 0, 150, 0.6),
              0 0 30px rgba(255, 0, 150, 0.4),
              0 0 40px rgba(255, 0, 150, 0.2);
          }
          16.6% {
            border-color: rgba(255, 255, 255, 0.1);
            box-shadow: 
              0 4px 6px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06),
              0 0 20px rgba(255, 128, 0, 0.6),
              0 0 30px rgba(255, 128, 0, 0.4),
              0 0 40px rgba(255, 128, 0, 0.2);
          }
          33.3% {
            border-color: rgba(255, 255, 255, 0.12);
            box-shadow: 
              0 4px 6px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06),
              0 0 20px rgba(255, 255, 0, 0.6),
              0 0 30px rgba(255, 255, 0, 0.4),
              0 0 40px rgba(255, 255, 0, 0.2);
          }
          50% {
            border-color: rgba(255, 255, 255, 0.1);
            box-shadow: 
              0 4px 6px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06),
              0 0 20px rgba(0, 255, 150, 0.6),
              0 0 30px rgba(0, 255, 150, 0.4),
              0 0 40px rgba(0, 255, 150, 0.2);
          }
          66.6% {
            border-color: rgba(255, 255, 255, 0.12);
            box-shadow: 
              0 4px 6px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06),
              0 0 20px rgba(0, 150, 255, 0.6),
              0 0 30px rgba(0, 150, 255, 0.4),
              0 0 40px rgba(0, 150, 255, 0.2);
          }
          83.3% {
            border-color: rgba(255, 255, 255, 0.1);
            box-shadow: 
              0 4px 6px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06),
              0 0 20px rgba(150, 0, 255, 0.6),
              0 0 30px rgba(150, 0, 255, 0.4),
              0 0 40px rgba(150, 0, 255, 0.2);
          }
          100% {
            border-color: rgba(255, 255, 255, 0.08);
            box-shadow: 
              0 4px 6px -1px rgba(0, 0, 0, 0.1),
              0 2px 4px -1px rgba(0, 0, 0, 0.06),
              0 0 20px rgba(255, 0, 150, 0.6),
              0 0 30px rgba(255, 0, 150, 0.4),
              0 0 40px rgba(255, 0, 150, 0.2);
          }
        }/* 移除彩虹色边框，改为纯发光效果 */
        .device-card-minimal::before {
          display: none;
        }

        .device-card-minimal::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 16px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
          z-index: -2;
        }        @keyframes rainbow-rotate {
          0% {
            --rainbow-angle: 0deg;
          }
          100% {
            --rainbow-angle: 360deg;
          }
        }

        @keyframes rainbow-rotate-2 {
          0% {
            --rainbow-angle-2: 0deg;
          }
          100% {
            --rainbow-angle-2: 360deg;
          }
        }

        @keyframes glow-border-rotate {
          0% {
            --glow-angle: 0deg;
          }
          100% {
            --glow-angle: 360deg;
          }
        }.device-card-minimal:active {
          transform: scale(0.98);
          box-shadow: 
            0 1px 3px 0 rgba(0, 0, 0, 0.1),
            0 1px 2px 0 rgba(0, 0, 0, 0.06),
            0 0 12px rgba(255, 255, 255, 0.15);
          animation-play-state: paused;
        }

        .device-card-elegant:active {
          animation-play-state: paused;
          box-shadow: 
            0 0 15px rgba(255, 255, 255, 0.1);
        }        /* 移除不同位置卡片的发光速度设置，因为已经禁用了发光边框 */        .device-card-elegant {
          position: relative;
          background: transparent;
          border-radius: 16px;
          padding: 12px;
          height: 260px;
          display: flex;
          flex-direction: column;
          overflow: visible;
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 
            0 0 12px rgba(255, 255, 255, 0.04);
          animation: device-glow-pulse 5s ease-in-out infinite;
        }@keyframes device-glow-pulse {
          0% {
            border-color: rgba(255, 255, 255, 0.08);
            box-shadow: 
              0 0 25px rgba(255, 0, 150, 0.5),
              0 0 35px rgba(255, 0, 150, 0.3),
              0 0 45px rgba(255, 0, 150, 0.2);
          }
          14.3% {
            border-color: rgba(255, 255, 255, 0.1);
            box-shadow: 
              0 0 25px rgba(255, 100, 0, 0.5),
              0 0 35px rgba(255, 100, 0, 0.3),
              0 0 45px rgba(255, 100, 0, 0.2);
          }
          28.6% {
            border-color: rgba(255, 255, 255, 0.12);
            box-shadow: 
              0 0 25px rgba(255, 200, 0, 0.5),
              0 0 35px rgba(255, 200, 0, 0.3),
              0 0 45px rgba(255, 200, 0, 0.2);
          }
          42.9% {
            border-color: rgba(255, 255, 255, 0.1);
            box-shadow: 
              0 0 25px rgba(150, 255, 0, 0.5),
              0 0 35px rgba(150, 255, 0, 0.3),
              0 0 45px rgba(150, 255, 0, 0.2);
          }
          57.1% {
            border-color: rgba(255, 255, 255, 0.12);
            box-shadow: 
              0 0 25px rgba(0, 255, 150, 0.5),
              0 0 35px rgba(0, 255, 150, 0.3),
              0 0 45px rgba(0, 255, 150, 0.2);
          }
          71.4% {
            border-color: rgba(255, 255, 255, 0.1);
            box-shadow: 
              0 0 25px rgba(0, 150, 255, 0.5),
              0 0 35px rgba(0, 150, 255, 0.3),
              0 0 45px rgba(0, 150, 255, 0.2);
          }
          85.7% {
            border-color: rgba(255, 255, 255, 0.12);
            box-shadow: 
              0 0 25px rgba(100, 0, 255, 0.5),
              0 0 35px rgba(100, 0, 255, 0.3),
              0 0 45px rgba(100, 0, 255, 0.2);
          }
          100% {
            border-color: rgba(255, 255, 255, 0.08);
            box-shadow: 
              0 0 25px rgba(255, 0, 150, 0.5),
              0 0 35px rgba(255, 0, 150, 0.3),
              0 0 45px rgba(255, 0, 150, 0.2);
          }
        }.device-card-elegant::before {
          display: none;
        }

        .device-card-elegant::after {
          content: '';
          position: absolute;
          top: 0px;
          left: 0px;
          right: 0px;
          bottom: 0px;
          border-radius: 16px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
          z-index: -2;
        }

        @keyframes device-glow-border-rotate {
          0% {
            --device-glow-angle: 0deg;
          }
          100% {
            --device-glow-angle: 360deg;
          }
        }        /* 移除点击时的发光效果，因为已经禁用了 ::before 伪元素 */

        /* ====== 移动端专用微动画 ====== */
        @media (hover: none) and (pointer: coarse) {
          .device-card-minimal {
            animation: subtle-float 4s ease-in-out infinite;
          }

          .device-card-minimal:nth-child(even) {
            animation-delay: 0.5s;
          }

          .device-card-minimal:nth-child(3n) {
            animation-delay: 1s;
          }
        }

        @keyframes subtle-float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-2px);
          }
        }

        /* ====== 桌面端悬停效果 ====== */
        @media (hover: hover) and (pointer: fine) {
          .device-card-minimal:hover {
            transform: translateY(-4px);
            box-shadow: 
              0 10px 15px -3px rgba(0, 0, 0, 0.1),
              0 4px 6px -2px rgba(0, 0, 0, 0.05);
            border-color: rgba(107, 114, 128, 0.4);
          }
        }        /* ====== 排行榜卡片基础样式与星光特效 ====== */
        
        .ranking-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
          perspective: 1000px;
        }

        .ranking-item {
          display: flex;
          align-items: center;
          padding: 20px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          position: relative;
          overflow: hidden;
          backdrop-filter: blur(15px);
          box-shadow: 
            0 4px 20px rgba(0, 0, 0, 0.3),
            0 8px 32px rgba(0, 0, 0, 0.15);
          opacity: 0;
          transform: translateY(20px);
          animation: ranking-appear 0.6s ease-out forwards;
        }

        @keyframes ranking-appear {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* 卡片内部星光粒子背景 */
        .ranking-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(1px 1px at 15px 20px, rgba(255, 255, 255, 0.4), transparent),
            radial-gradient(2px 2px at 85px 30px, rgba(255, 255, 255, 0.2), transparent),
            radial-gradient(1px 1px at 150px 45px, rgba(255, 255, 255, 0.6), transparent),
            radial-gradient(2px 2px at 200px 25px, rgba(255, 255, 255, 0.3), transparent),
            radial-gradient(1px 1px at 50px 55px, rgba(255, 255, 255, 0.5), transparent),
            radial-gradient(2px 2px at 120px 60px, rgba(255, 255, 255, 0.4), transparent);
          background-size: 240px 80px;
          animation: card-stars-twinkle 4s ease-in-out infinite;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
          z-index: 1;
        }        @keyframes card-stars-twinkle {
          0%, 100% { 
            opacity: 0;
            transform: scale(1);
          }
          40% {
            opacity: 0.5;
            transform: scale(1.03);
          }
          60% { 
            opacity: 0.7;
            transform: scale(1);
          }
        }

        /* 卡片光影扫过效果 */
        .ranking-item::after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg, 
            transparent, 
            rgba(255, 255, 255, 0.15) 40%,
            rgba(255, 255, 255, 0.3) 50%,
            rgba(255, 255, 255, 0.15) 60%,
            transparent
          );
          transition: left 0.8s ease;
          z-index: 3;
          pointer-events: none;
        }

        /* 悬浮状态激活特效 */
        .ranking-item:hover {
          transform: translateY(-4px) scale(1.02);
          box-shadow: 
            0 12px 40px rgba(0, 0, 0, 0.4),
            0 20px 60px rgba(0, 0, 0, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .ranking-item:hover::before {
          opacity: 1;
          animation-duration: 2.5s;
        }

        .ranking-item:hover::after {
          left: 100%;
        }

        /* 排名延迟出现动画 */
        .ranking-item:nth-child(1) { animation-delay: 0.1s; }
        .ranking-item:nth-child(2) { animation-delay: 0.2s; }
        .ranking-item:nth-child(3) { animation-delay: 0.3s; }

        /* ====== 排名徽章样式与星光特效 ====== */
        
        .rank-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-width: 70px;
          height: 70px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          font-weight: 800;
          font-size: 1.3rem;
          margin-right: 20px;
          box-shadow: 
            0 8px 24px rgba(0, 0, 0, 0.2),
            inset 0 2px 0 rgba(255, 255, 255, 0.2);
          transition: all 0.4s ease;
          position: relative;
          overflow: hidden;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #ffffff;
          animation: badge-float 3s ease-in-out infinite;
        }

        @keyframes badge-float {
          0%, 100% { 
            transform: translateY(0) rotate(0deg);
          }
          25% { 
            transform: translateY(-3px) rotate(1deg);
          }
          75% { 
            transform: translateY(3px) rotate(-1deg);
          }
        }

        /* 徽章旋转光圈 */
        .rank-badge::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: conic-gradient(
            from 0deg, 
            transparent, 
            rgba(255, 255, 255, 0.2), 
            transparent
          );
          animation: badge-rotate 4s linear infinite;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        @keyframes badge-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* 徽章星光粒子 */
        .rank-badge::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: 
            radial-gradient(1px 1px at 15px 15px, rgba(255, 215, 0, 0.9), transparent),
            radial-gradient(1px 1px at 35px 25px, rgba(255, 215, 0, 0.7), transparent),
            radial-gradient(1px 1px at 25px 45px, rgba(255, 215, 0, 1), transparent),
            radial-gradient(1px 1px at 45px 55px, rgba(255, 215, 0, 0.8), transparent),
            radial-gradient(1px 1px at 55px 35px, rgba(255, 215, 0, 0.9), transparent);
          background-size: 70px 70px;
          animation: badge-sparkle 2.5s ease-in-out infinite;
          opacity: 0;
          pointer-events: none;
          border-radius: 50%;
        }        @keyframes badge-sparkle {
          0%, 100% { 
            opacity: 0;
            transform: scale(0.9);
          }
          50% { 
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        /* 悬浮时激活徽章特效 */
        .ranking-item:hover .rank-badge::before,
        .ranking-item:active .rank-badge::before {
          opacity: 1;
        }

        .ranking-item:hover .rank-badge::after,
        .ranking-item:active .rank-badge::after {
          opacity: 1;
          animation-duration: 1.5s;
        }

        .ranking-item:hover .rank-badge,
        .ranking-item:active .rank-badge {
          transform: scale(1.1) rotate(5deg);
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
          animation-play-state: paused;
          box-shadow: 
            0 12px 36px rgba(0, 0, 0, 0.3),
            inset 0 2px 0 rgba(255, 255, 255, 0.4),
            0 0 30px rgba(255, 255, 255, 0.3);
        }

        /* 前三名徽章专属样式 */
        .rank-1 .rank-badge {
          background: rgba(251, 191, 36, 0.2);
          border-color: rgba(251, 191, 36, 0.4);
          color: #fbbf24;
          text-shadow: 
            0 2px 4px rgba(0, 0, 0, 0.5),
            0 0 20px rgba(251, 191, 36, 0.5);
          box-shadow: 
            0 8px 24px rgba(251, 191, 36, 0.3),
            inset 0 2px 0 rgba(255, 255, 255, 0.3);
        }

        .rank-1 .rank-badge::after {
          background: 
            radial-gradient(1px 1px at 15px 15px, rgba(255, 215, 0, 0.9), transparent),
            radial-gradient(1px 1px at 35px 25px, rgba(255, 215, 0, 0.7), transparent),
            radial-gradient(1px 1px at 25px 45px, rgba(255, 215, 0, 1), transparent),
            radial-gradient(1px 1px at 45px 55px, rgba(255, 215, 0, 0.8), transparent),
            radial-gradient(1px 1px at 55px 35px, rgba(255, 215, 0, 0.9), transparent);
        }

        .rank-2 .rank-badge {
          background: rgba(156, 163, 175, 0.2);
          border-color: rgba(156, 163, 175, 0.4);
          color: #9ca3af;
          text-shadow: 
            0 2px 4px rgba(0, 0, 0, 0.5),
            0 0 20px rgba(156, 163, 175, 0.5);
          box-shadow: 
            0 8px 24px rgba(156, 163, 175, 0.3),
            inset 0 2px 0 rgba(255, 255, 255, 0.3);
        }

        .rank-2 .rank-badge::after {
          background: 
            radial-gradient(1px 1px at 15px 15px, rgba(192, 192, 192, 0.9), transparent),
            radial-gradient(1px 1px at 35px 25px, rgba(192, 192, 192, 0.7), transparent),
            radial-gradient(1px 1px at 25px 45px, rgba(192, 192, 192, 1), transparent),
            radial-gradient(1px 1px at 45px 55px, rgba(192, 192, 192, 0.8), transparent),
            radial-gradient(1px 1px at 55px 35px, rgba(192, 192, 192, 0.9), transparent);
        }

        .rank-3 .rank-badge {
          background: rgba(251, 146, 60, 0.2);
          border-color: rgba(251, 146, 60, 0.4);
          color: #fb923c;
          text-shadow: 
            0 2px 4px rgba(0, 0, 0, 0.5),
            0 0 20px rgba(251, 146, 60, 0.5);
          box-shadow: 
            0 8px 24px rgba(251, 146, 60, 0.3),
            inset 0 2px 0 rgba(255, 255, 255, 0.3);
        }        .rank-3 .rank-badge::after {
          background: 
            radial-gradient(1px 1px at 15px 15px, rgba(205, 127, 50, 0.9), transparent),
            radial-gradient(1px 1px at 35px 25px, rgba(205, 127, 50, 0.7), transparent),
            radial-gradient(1px 1px at 25px 45px, rgba(205, 127, 50, 1), transparent),
            radial-gradient(1px 1px at 45px 55px, rgba(205, 127, 50, 0.8), transparent),
            radial-gradient(1px 1px at 55px 35px, rgba(205, 127, 50, 0.9), transparent);
        }

        /* ====== 高端星光排行榜模态框主题样式 ====== */
        
        /* 黑色主题背景 */
        .rankings-modal.dark-theme .modal-dialog {
          max-width: 90vw;
          width: 90vw;
          margin: 0 auto;
        }

        .rankings-modal.dark-theme .modal-content {
          background: linear-gradient(135deg, #0f0f23 0%, #161629 50%, #1a1a2e 100%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.8),
            0 0 100px rgba(75, 0, 130, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          position: relative;
          overflow: hidden;
        }

        /* 背景星空效果 */
        .rankings-modal.dark-theme .modal-content::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(2px 2px at 20px 30px, rgba(255, 255, 255, 0.3), transparent),
            radial-gradient(2px 2px at 40px 70px, rgba(255, 255, 255, 0.2), transparent),
            radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.4), transparent),
            radial-gradient(1px 1px at 130px 80px, rgba(255, 255, 255, 0.3), transparent),
            radial-gradient(2px 2px at 160px 30px, rgba(255, 255, 255, 0.2), transparent),
            radial-gradient(1px 1px at 200px 60px, rgba(255, 255, 255, 0.4), transparent),
            radial-gradient(2px 2px at 240px 90px, rgba(255, 255, 255, 0.3), transparent),
            radial-gradient(1px 1px at 280px 20px, rgba(255, 255, 255, 0.2), transparent),
            radial-gradient(1px 1px at 320px 70px, rgba(255, 255, 255, 0.4), transparent),
            radial-gradient(2px 2px at 360px 40px, rgba(255, 255, 255, 0.3), transparent),
            radial-gradient(1px 1px at 400px 80px, rgba(255, 255, 255, 0.2), transparent),
            radial-gradient(2px 2px at 440px 50px, rgba(255, 255, 255, 0.4), transparent);
          background-size: 480px 120px;
          animation: modal-stars-drift 8s linear infinite;
          opacity: 0.6;
          pointer-events: none;
          z-index: 1;
        }

        @keyframes modal-stars-drift {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(-60px) translateY(-20px); }
        }

        /* 模态框头部样式 */
        .rankings-modal.dark-theme .modal-header {
          background: transparent;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding: 24px 28px;
          position: relative;
          z-index: 2;
        }

        .rankings-modal.dark-theme .modal-title {
          color: #ffffff;
          font-size: 1.3rem;
          font-weight: 700;
          text-shadow: 
            0 2px 4px rgba(0, 0, 0, 0.5),
            0 0 20px rgba(255, 255, 255, 0.3);
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .rankings-modal.dark-theme .btn-close {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          width: 36px;
          height: 36px;
          opacity: 0.8;
          transition: all 0.3s ease;
        }

        .rankings-modal.dark-theme .btn-close:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: scale(1.1);
          opacity: 1;
        }

        /* 模态框主体样式 */
        .rankings-modal.dark-theme .modal-body {
          background: transparent;
          padding: 28px;
          position: relative;
          z-index: 2;
          max-height: 70vh;
          overflow-y: auto;
        }

        /* 统计信息卡片 */
        .rankings-modal.dark-theme .bg-light {
          background: rgba(255, 255, 255, 0.08) !important;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          backdrop-filter: blur(10px);
          position: relative;
          overflow: hidden;
        }

        .rankings-modal.dark-theme .bg-light::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(1px 1px at 20px 15px, rgba(255, 255, 255, 0.3), transparent),
            radial-gradient(1px 1px at 60px 25px, rgba(255, 255, 255, 0.2), transparent),
            radial-gradient(1px 1px at 100px 35px, rgba(255, 255, 255, 0.4), transparent);
          background-size: 120px 50px;
          animation: stats-sparkle 3s ease-in-out infinite;
          opacity: 0.5;
          pointer-events: none;
        }

        @keyframes stats-sparkle {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }

        .rankings-modal.dark-theme .text-primary {
          color: #60a5fa !important;
          text-shadow: 0 0 10px rgba(96, 165, 250, 0.5);
        }

        .rankings-modal.dark-theme .text-success {
          color: #34d399 !important;
          text-shadow: 0 0 10px rgba(52, 211, 153, 0.5);
        }

        .rankings-modal.dark-theme .text-muted {
          color: #9ca3af !important;
        }

        /* 排行榜标题样式优化 */
        .rankings-modal.dark-theme .ranking-section-title {
          font-size: 1.3rem;
          font-weight: 700;
          margin-bottom: 16px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .rankings-modal.dark-theme .burst-title {
          color: #ff6b6b;
          text-shadow: 
            0 2px 4px rgba(0, 0, 0, 0.5),
            0 0 15px rgba(255, 107, 107, 0.5);
        }

        .rankings-modal.dark-theme .resistance-title {
          color: #4ecdc4;
          text-shadow: 
            0 2px 4px rgba(0, 0, 0, 0.5),
            0 0 15px rgba(78, 205, 196, 0.5);
        }

        .rankings-modal.dark-theme .ranking-subtitle {
          color: #94a3b8 !important;
          font-size: 0.85rem;
          font-weight: 400;
          opacity: 0.9;
        }

        /* 排行榜卡片布局优化 */
        .rankings-modal.dark-theme .rank-badge {
          flex-shrink: 0;
          width: 70px;
          height: 70px;
          margin-right: 0;
        }

        .rankings-modal.dark-theme .device-info {
          flex: 1;
          min-width: 0;
          margin-right: 16px;
        }

        .rankings-modal.dark-theme .stats {
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          margin-right: 16px;
          min-width: 80px;
        }

        .rankings-modal.dark-theme .primary-stat,
        .rankings-modal.dark-theme .secondary-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .rankings-modal.dark-theme .status-badge {
          flex-shrink: 0;
          min-width: 70px;
          text-align: center;
        }

        /* 排行榜列表增强 */
        .rankings-modal.dark-theme .ranking-list {
          gap: 20px;
        }        .rankings-modal.dark-theme .ranking-item {
          display: flex;
          align-items: center;
          padding: 24px 20px;
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 18px;
          backdrop-filter: blur(15px);
          position: relative;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          transform: translateY(0);
          gap: 20px;
          min-height: 90px;
        }/* 排行榜卡片星光背景 - 减少动画频率 */
        .rankings-modal.dark-theme .ranking-item::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(1px 1px at 15px 20px, rgba(255, 255, 255, 0.3), transparent),
            radial-gradient(1px 1px at 85px 30px, rgba(255, 255, 255, 0.2), transparent),
            radial-gradient(1px 1px at 150px 45px, rgba(255, 255, 255, 0.4), transparent),
            radial-gradient(2px 2px at 200px 25px, rgba(255, 255, 255, 0.2), transparent),
            radial-gradient(1px 1px at 50px 55px, rgba(255, 255, 255, 0.3), transparent),
            radial-gradient(2px 2px at 120px 60px, rgba(255, 255, 255, 0.2), transparent);
          background-size: 280px 90px;
          animation: ranking-stars-twinkle 6s ease-in-out infinite;
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
          z-index: 1;
        }@keyframes ranking-stars-twinkle {
          0%, 100% { 
            opacity: 0;
            transform: scale(1);
          }
          30% {
            opacity: 0.4;
            transform: scale(1.02);
          }
          50% { 
            opacity: 0.8;
            transform: scale(1);
          }
          70% {
            opacity: 0.6;
            transform: scale(0.98);
          }
        }

        /* 卡片光影扫过效果 */
        .rankings-modal.dark-theme .ranking-item::after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg, 
            transparent, 
            rgba(255, 255, 255, 0.1) 40%,
            rgba(255, 255, 255, 0.2) 50%,
            rgba(255, 255, 255, 0.1) 60%,
            transparent
          );
          transition: left 0.8s ease;
          z-index: 3;
          pointer-events: none;
        }        /* 悬浮效果优化 - 减少动画强度 */
        .rankings-modal.dark-theme .ranking-item:hover {
          transform: translateY(-3px) scale(1.01);
          box-shadow: 
            0 15px 30px rgba(0, 0, 0, 0.4),
            0 20px 45px rgba(0, 0, 0, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.2);
        }

        .rankings-modal.dark-theme .ranking-item:hover::before {
          opacity: 0.6;
          animation-duration: 4s;
        }

        .rankings-modal.dark-theme .ranking-item:hover::after {
          left: 100%;
        }

        /* 排名徽章增强 */
        .rankings-modal.dark-theme .rank-badge {
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 
            0 10px 30px rgba(0, 0, 0, 0.3),
            inset 0 2px 0 rgba(255, 255, 255, 0.2);
          position: relative;
          z-index: 2;
        }

        /* 排名第一特殊效果 */
        .rankings-modal.dark-theme .rank-1 .rank-badge {
          background: rgba(255, 215, 0, 0.15);
          border-color: rgba(255, 215, 0, 0.4);
          color: #ffd700;
          text-shadow: 
            0 2px 4px rgba(0, 0, 0, 0.5),
            0 0 20px rgba(255, 215, 0, 0.6);
          box-shadow: 
            0 10px 30px rgba(255, 215, 0, 0.4),
            inset 0 2px 0 rgba(255, 255, 255, 0.3),
            0 0 40px rgba(255, 215, 0, 0.3);
        }

        .rankings-modal.dark-theme .rank-2 .rank-badge {
          background: rgba(192, 192, 192, 0.15);
          border-color: rgba(192, 192, 192, 0.4);
          color: #c0c0c0;
          text-shadow: 
            0 2px 4px rgba(0, 0, 0, 0.5),
            0 0 20px rgba(192, 192, 192, 0.6);
          box-shadow: 
            0 10px 30px rgba(192, 192, 192, 0.4),
            inset 0 2px 0 rgba(255, 255, 255, 0.3),
            0 0 40px rgba(192, 192, 192, 0.3);
        }

        .rankings-modal.dark-theme .rank-3 .rank-badge {
          background: rgba(205, 127, 50, 0.15);
          border-color: rgba(205, 127, 50, 0.4);
          color: #cd7f32;
          text-shadow: 
            0 2px 4px rgba(0, 0, 0, 0.5),
            0 0 20px rgba(205, 127, 50, 0.6);
          box-shadow: 
            0 10px 30px rgba(205, 127, 50, 0.4),
            inset 0 2px 0 rgba(255, 255, 255, 0.3),
            0 0 40px rgba(205, 127, 50, 0.3);
        }        /* 设备信息文字样式优化 */
        .rankings-modal.dark-theme .device-name {
          color: #f8fafc;
          font-weight: 700;
          font-size: 1.1rem;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.6);
          margin-bottom: 4px;
        }

        .rankings-modal.dark-theme .device-number {
          color: #cbd5e1;
          font-size: 0.9rem;
          font-weight: 500;
          opacity: 0.9;
        }        /* 统计数据样式优化 */
        .rankings-modal.dark-theme .stats .value {
          color: #60a5fa;
          font-weight: 800;
          font-size: 1.2rem;
          text-shadow: 0 0 8px rgba(96, 165, 250, 0.4);
        }

        .rankings-modal.dark-theme .stats .label {
          color: #e2e8f0;
          font-size: 0.85rem;
          font-weight: 500;
          opacity: 0.9;
        }/* 状态徽章样式优化 */
        .rankings-modal.dark-theme .status-badge {
          background: rgba(100, 116, 139, 0.15);
          color: #94a3b8;
          border: 1px solid rgba(100, 116, 139, 0.3);
          backdrop-filter: blur(5px);
          padding: 6px 12px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.85rem;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
        }

        .rankings-modal.dark-theme .status-badge.idle {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
          border-color: rgba(34, 197, 94, 0.4);
          text-shadow: 0 0 8px rgba(34, 197, 94, 0.6);
          box-shadow: 0 0 12px rgba(34, 197, 94, 0.3);
        }

        .rankings-modal.dark-theme .status-badge.jackpot {
          background: rgba(255, 165, 0, 0.25);
          color: #ff8c00;
          border-color: rgba(255, 165, 0, 0.5);
          text-shadow: 0 0 10px rgba(255, 165, 0, 0.7);
          box-shadow: 0 0 15px rgba(255, 165, 0, 0.4);
          animation: jackpot-glow 2s ease-in-out infinite;
        }

        @keyframes jackpot-glow {
          0%, 100% { 
            box-shadow: 0 0 15px rgba(255, 165, 0, 0.4);
          }
          50% { 
            box-shadow: 0 0 25px rgba(255, 165, 0, 0.6);
          }
        }

        .rankings-modal.dark-theme .status-badge.occupied {
          background: rgba(239, 68, 68, 0.25);
          color: #f87171;
          border-color: rgba(239, 68, 68, 0.5);
          text-shadow: 0 0 10px rgba(239, 68, 68, 0.6);
          box-shadow: 0 0 12px rgba(239, 68, 68, 0.3);
        }

        /* 空状态样式 */
        .rankings-modal.dark-theme .text-center.text-muted {
          color: #6b7280 !important;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        /* 模态框底部样式 */
        .rankings-modal.dark-theme .modal-footer {
          background: transparent;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding: 20px 28px;
          position: relative;
          z-index: 2;
        }

        .rankings-modal.dark-theme .modal-footer .btn {
          border-radius: 10px;
          padding: 10px 24px;
          font-weight: 600;
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .rankings-modal.dark-theme .modal-footer .btn-secondary {
          background: rgba(255, 255, 255, 0.1);
          color: #e5e7eb;
        }

        .rankings-modal.dark-theme .modal-footer .btn-secondary:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
        }

        .rankings-modal.dark-theme .modal-footer .btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: #ffffff;
          border-color: rgba(59, 130, 246, 0.5);
        }

        .rankings-modal.dark-theme .modal-footer .btn-primary:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
          transform: translateY(-2px);
          box-shadow: 
            0 8px 25px rgba(59, 130, 246, 0.4),
            0 0 20px rgba(59, 130, 246, 0.3);
        }

        /* 加载状态样式 */
        .rankings-modal.dark-theme .spinner-border {
          color: #60a5fa;
          width: 3rem;
          height: 3rem;
        }

        .rankings-modal.dark-theme .text-center p {
          color: #9ca3af;
          font-size: 1.1rem;
          margin-top: 16px;
        }

        /* 添加CSS动画样式 */
        @keyframes pachinkoGlow {
          from { box-shadow: 0 0 20px rgba(255, 215, 0, 0.8), inset 0 2px 8px rgba(255, 255, 255, 0.3); }
          to { box-shadow: 0 0 30px rgba(255, 215, 0, 1), inset 0 2px 8px rgba(255, 255, 255, 0.5); }
        }

        @keyframes pachinkoSweep {
          0% { transform: translateX(-100%) skewX(-45deg); }
          50% { transform: translateX(0%) skewX(-45deg); }
          100% { transform: translateX(100%) skewX(-45deg); }
        }

        @keyframes pachinkoSparkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }

        .pachinko-card-container:hover {
          transform: translateY(-5px) scale(1.02);
          box-shadow: 
            0 12px 40px rgba(255, 215, 0, 0.4),
            inset 0 2px 8px rgba(255, 215, 0, 0.3),
            0 0 30px rgba(255, 215, 0, 0.2) !important;
        }

        .pachinko-card-container:active {
          transform: translateY(-2px) scale(0.98);
        }
      `}</style>
      {/* 粒子特效相关DOM和脚本已移除 */}
    </div>
  );
}

/* ---------------- 极简黑灰样式 ---------------- */
const styles = {
  pageWrapper: {
    position: 'relative',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
  },
  innerContainer: {
    maxWidth: '900px',
    margin: '0 auto',
    color: '#f8fafc',
    padding: '20px 20px 70px'
  },
  pageTitle: {
    textAlign: 'center',
    marginBottom: '30px',
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#f1f5f9',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  rankingBtn: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    border: 'none',
    color: 'white',
    fontWeight: '600',
    padding: '8px 16px',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    transition: 'all 0.3s ease'
  },
  doublePlayEnterWrapper: {
    marginTop: '12px',
    display: 'flex',
    justifyContent: 'center'
  },
  doublePlayEnterBtn: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#fff',
    fontWeight: 600,
    boxShadow: '0 4px 12px rgba(245,158,11,0.4)',
    letterSpacing: '1px'
  },
  deviceCard: {
    position: 'relative',
    background: 'transparent',
    borderRadius: '16px',
    padding: '12px',
    border: 'none',
    overflow: 'hidden',
    cursor: 'pointer',
    height: '260px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    boxSizing: 'border-box',
  },
  coverWrap: {
    width: '100%',
    marginBottom: '10px',
    height: '110px',
    overflow: 'hidden',
    flexShrink: 0,
    position: 'relative',
    borderRadius: '12px',
    border: '1px solid rgba(107,114,128,0.2)',
    background: 'rgba(31, 41, 55, 0.5)',
  },
  coverImage: {
    width: '100%',
    height: '110px',
    borderRadius: '12px',
    objectFit: 'cover',
    display: 'block',
    filter: 'brightness(0.9) saturate(1.1)',
    transition: 'filter 0.3s ease',
  },
  coverHistBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 3,
    background: 'rgba(255,255,255,0.9)',
    color: '#6b7280',
    borderRadius: '8px',
    width: 36,
    height: 28,
    padding: 0,
    fontWeight: '500',
    fontSize: 12,
    border: '1px solid rgba(107,114,128,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  coverStatus: {
    position: 'absolute',
    left: 8,
    bottom: 8,
    minWidth: 50,
    padding: '2px 8px',
    borderRadius: 6,
    color: '#fff',
    fontWeight: '500',
    fontSize: 12,
    textAlign: 'center',
    zIndex: 3,
    letterSpacing: 0.5,
    userSelect: 'none',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  coverRemainTime: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    minWidth: 35,
    padding: '2px 6px',
    borderRadius: 6,
    color: '#f8fafc',
    fontWeight: '500',
    fontSize: 12,
    background: 'rgba(30, 41, 59, 0.8)',
    border: '1px solid rgba(107, 114, 128, 0.3)',
    textAlign: 'center',
    zIndex: 3,
    userSelect: 'none',
  },  cardTableWrap: {
    width: '100%',
    overflowX: 'visible',
    flex: 1,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    padding: '4px 0',
  },infoTable: {
    width: '100%',
    color: '#e2e8f0',
    fontSize: '13px',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    overflow: 'hidden',
    background: 'transparent',
    borderRadius: '8px',
    border: 'none',
    lineHeight: '1.3',
    height: '100%',
  },
  cardTdLabel: {
    whiteSpace: 'normal',
    overflow: 'visible',
    width: '35%',
    fontWeight: '600',
    fontSize: 13,
    color: '#94a3b8',
    padding: '4px 8px',
    lineHeight: '1.3',
    verticalAlign: 'top',
    wordBreak: 'break-word',
  },
  cardTdValue: {
    whiteSpace: 'normal',
    overflow: 'visible',
    width: '65%',
    fontSize: 13,
    padding: '4px 8px',
    color: '#f1f5f9',
    fontWeight: '600',
    lineHeight: '1.3',
    verticalAlign: 'top',
    wordBreak: 'break-word',
  },  footerBar: {
    position: 'fixed',
    bottom: 0,
    left:  0,
    right: 0,
    height: 65,
    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 50%, rgba(15, 23, 42, 0.98) 100%)',
    borderTop: '1px solid rgba(75, 85, 99, 0.4)',
    display: 'flex',
    alignItems: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(20px)',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
    padding: '0',
  },
  footerBtnCol: {
    display: 'flex',
    justifyContent: 'center',
    padding: '0',
    margin: '0',
    flexShrink: 0,
    flex: 1,
  },
  footerBtn: {
    width: '100%',
    minWidth: 'auto',
    maxWidth: 'none',
    fontWeight: '600',
    fontSize: '12px',
    position: 'relative',
    zIndex: 2,
    padding: '12px 6px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    margin: '4px 2px',
    height: '55px',
  },

  // ========== 弹珠机风格样式 ==========
  pachinkoCard: {
    position: 'relative',
    background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    borderRadius: '20px',
    padding: '0',
    border: '3px solid #ffd700',
    overflow: 'hidden',
    cursor: 'pointer',
    height: '450px', // 增加高度以容纳封面图片
    boxShadow: `
      0 8px 32px rgba(255, 215, 0, 0.3),
      inset 0 2px 8px rgba(255, 215, 0, 0.2),
      0 0 20px rgba(255, 215, 0, 0.1)
    `,
    transition: 'all 0.3s ease',
    backgroundImage: `
      radial-gradient(circle at 20% 20%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 105, 180, 0.1) 0%, transparent 50%)
    `,
  },

  pachinkoHeader: {
    background: 'linear-gradient(135deg, #ffd700 0%, #ffb347 50%, #ff8c00 100%)',
    padding: '8px 12px',
    borderBottom: '2px solid #ff8c00',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '40px',
  },

  pachinkoTitle: {
    textAlign: 'left',
    position: 'relative',
    zIndex: 2,
    flex: 1,
    overflow: 'hidden',
    marginRight: '8px',
  },

  pachinkoTitleText: {
    fontSize: '14px',
    fontWeight: '900',
    color: '#8b0000',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
    letterSpacing: '0.5px',
    fontFamily: "'Arial Black', sans-serif",
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    maxWidth: '100%',
  },

  // 新增：设备封面图片区域
  pachinkoCoverArea: {
    position: 'relative',
    height: '120px',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #2d1b69 0%, #11998e 100%)',
    borderBottom: '2px solid #ffd700',
  },

  pachinkoCoverImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    filter: 'brightness(0.8) saturate(1.1)',
    transition: 'filter 0.3s ease',
  },

  pachinkoRemainTime: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    padding: '4px 8px',
    borderRadius: '8px',
    background: 'rgba(0, 0, 0, 0.7)',
    color: '#ffd700',
    fontSize: '12px',
    fontWeight: '700',
    border: '1px solid rgba(255, 215, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
  },

  pachinkoStatsArea: {
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
    padding: '12px 16px',
    borderBottom: '2px solid #1e40af',
    flex: 1,
  },

  pachinkoStatsTitle: {
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '800',
    color: '#ffd700',
    marginBottom: '10px',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
    letterSpacing: '1px',
  },

  pachinkoStatsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '6px',
  },

  pachinkoStatItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.1)',
    padding: '6px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 215, 0, 0.3)',
    textAlign: 'center',
  },

  pachinkoStatLabel: {
    fontSize: '10px',
    color: '#e0e7ff',
    fontWeight: '600',
    marginBottom: '2px',
    wordBreak: 'break-word',
  },

  pachinkoStatValue: {
    fontSize: '11px',
    color: '#ffd700',
    fontWeight: '800',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
    wordBreak: 'break-word',
  },

  pachinkoMiniHistoryArea: {
    background: 'linear-gradient(135deg, #2d1b69 0%, #11998e 50%, #1e3a8a 100%)',
    padding: '0px', /* 完全取消内边距 */
    borderBottom: '2px solid #ffd700',
    borderTop: '1px solid rgba(255, 215, 0, 0.3)',
    height: '130px', /* 增加高度以充分利用空间 */
    display: 'flex',
    alignItems: 'stretch', /* 让子元素完全填满 */
  },

  pachinkoStatusBadge: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    padding: '4px 8px',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '11px',
    fontWeight: '700',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    zIndex: 10,
    backdropFilter: 'blur(4px)',
  },

  pachinkoHistBtn: {
    position: 'absolute',
    bottom: '12px',
    right: '12px',
    background: 'linear-gradient(135deg, #ffd700 0%, #ffb347 100%)',
    border: '2px solid #ff8c00',
    color: '#8b0000',
    fontSize: '11px',
    fontWeight: '800',
    padding: '4px 8px',
    borderRadius: '8px',
    textShadow: '1px 1px 2px rgba(255, 255, 255, 0.5)',
    boxShadow: '0 2px 8px rgba(255, 215, 0, 0.4)',
    zIndex: 10,
    transition: 'all 0.2s ease',
  },

  // 新增：Header中的履历按钮样式
  pachinkoHistBtnInHeader: {
    background: 'linear-gradient(135deg, #dc143c 0%, #b22222 100%)',
    border: '2px solid #8b0000',
    color: '#fff',
    fontSize: '10px',
    fontWeight: '800',
    padding: '3px 8px',
    borderRadius: '6px',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.7)',
    boxShadow: '0 2px 8px rgba(220, 20, 60, 0.4)',
    zIndex: 10,
    transition: 'all 0.2s ease',
    minWidth: '50px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },

  pachinkoGlowEffect: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'linear-gradient(45deg, transparent 48%, rgba(255, 215, 0, 0.1) 50%, transparent 52%)',
    animation: 'pachinkoSweep 3s ease-in-out infinite',
    pointerEvents: 'none',
  },

  pachinkoSparkles: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    pointerEvents: 'none',
  },

  pachinkoSparkle1: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    fontSize: '12px',
    animation: 'pachinkoSparkle 2s ease-in-out infinite',
  },

  pachinkoSparkle2: {
    position: 'absolute',
    top: '50%',
    left: '20px',
    fontSize: '16px',
    animation: 'pachinkoSparkle 2.5s ease-in-out infinite 0.5s',
  },

  pachinkoSparkle3: {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    fontSize: '10px',
    animation: 'pachinkoSparkle 1.8s ease-in-out infinite 1s',
  },

  // 底部状态显示区域
  pachinkoBottomStatus: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '40px',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    borderTop: '2px solid #ffd700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  pachinkoStatusText: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#ffd700',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
    letterSpacing: '1px',
  },
};

/* ====== 底部按键高端样式 - 全面优化版 ====== */
const additionalStyles = `
  .footer-btn-premium {
    position: relative;
    overflow: hidden;
    border-width: 2px !important;
    font-weight: 700 !important;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    backdrop-filter: blur(15px);
    border-radius: 12px !important;
    box-shadow: 
      0 6px 20px rgba(0, 0, 0, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.15),
      0 0 0 1px rgba(255, 255, 255, 0.05);
    user-select: none;
    cursor: pointer;
    will-change: transform;
    width: 100% !important;
    margin: 4px 2px !important;
    padding: 14px 8px !important;
    min-height: 55px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-direction: row !important;
  }

  /* 按键发光效果 */
  .footer-btn-premium::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.6s ease;
    z-index: 1;
  }

  /* 悬浮光束效果 */
  .footer-btn-premium::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%);
    border-radius: 50%;
    transition: all 0.3s ease;
    transform: translate(-50%, -50%);
    z-index: 0;
  }

  .footer-btn-premium:hover::before {
    left: 100%;
  }

  .footer-btn-premium:hover::after {
    width: 120%;
    height: 120%;
  }

  .footer-btn-premium:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 
      0 12px 35px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.25),
      0 0 20px rgba(255, 255, 255, 0.1);
  }

  .footer-btn-premium:active {
    transform: translateY(-1px) scale(0.98);
    transition: transform 0.1s ease;
    box-shadow: 
      0 4px 15px rgba(0, 0, 0, 0.3),
      inset 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  /* 按键文字样式优化 */
  .footer-btn-premium .btn-text {
    position: relative;
    z-index: 2;
    font-family: 'Segoe UI', 'Microsoft YaHei', sans-serif;
    font-weight: 800;
    font-size: 0.9rem !important;
    text-shadow: 
      0 1px 2px rgba(0, 0, 0, 0.6),
      0 0 8px rgba(255, 255, 255, 0.2);
    margin-left: 4px;
  }

  .footer-btn-premium svg {
    position: relative;
    z-index: 2;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
    transition: all 0.3s ease;
    font-size: 1.1rem !important;
    margin-right: 6px !important;
  }

  .footer-btn-premium:hover svg {
    transform: scale(1.1) rotate(5deg);
  }

  /* 去除Bootstrap默认的Col padding */
  .footer-btn-premium-col {
    padding-left: 2px !important;
    padding-right: 2px !important;
  }

  /* 大厅按键 - 金色主题 */
  .footer-btn-home {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%) !important;
    border-color: #fbbf24 !important;
    color: #1f2937 !important;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
  }

  .footer-btn-home:hover {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%) !important;
    border-color: #f59e0b !important;
    color: #1f2937 !important;
    box-shadow: 
      0 12px 35px rgba(251, 191, 36, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.3),
      0 0 25px rgba(251, 191, 36, 0.3);
  }

  /* 我的设备按键 - 红色主题 */
  .footer-btn-devices {
    background: linear-gradient(135deg, #f87171 0%, #ef4444 50%, #dc2626 100%) !important;
    border-color: #f87171 !important;
    color: #ffffff !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
  }

  .footer-btn-devices:hover {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%) !important;
    border-color: #ef4444 !important;
    color: #ffffff !important;
    box-shadow: 
      0 12px 35px rgba(248, 113, 113, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.25),
      0 0 25px rgba(248, 113, 113, 0.3);
  }

  /* 游戏明细按键 - 青色主题 */
  .footer-btn-logs {
    background: linear-gradient(135deg, #67e8f9 0%, #06b6d4 50%, #0891b2 100%) !important;
    border-color: #67e8f9 !important;
    color: #1f2937 !important;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
  }

  .footer-btn-logs:hover {
    background: linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%) !important;
    border-color: #06b6d4 !important;
    color: #ffffff !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
    box-shadow: 
      0 12px 35px rgba(103, 232, 249, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.25),
      0 0 25px rgba(103, 232, 249, 0.3);
  }

  /* 个人信息按键 - 蓝色主题 */
  .footer-btn-profile {
    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%) !important;
    border-color: #60a5fa !important;
    color: #ffffff !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
  }

  .footer-btn-profile:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%) !important;
    border-color: #3b82f6 !important;
    color: #ffffff !important;
    box-shadow: 
      0 12px 35px rgba(96, 165, 250, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.25),
      0 0 25px rgba(96, 165, 250, 0.3);
  }

  /* 按键脉冲动画 */
  @keyframes button-pulse {
    0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4); }
    50% { box-shadow: 0 0 0 8px rgba(255, 255, 255, 0.1); }
    100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
  }

  .footer-btn-premium:focus {
    animation: button-pulse 0.8s ease-out;
    outline: none;
  }

  /* 平板端优化 */
  @media (max-width: 1024px) {
    .footer-btn-premium {
      padding: 12px 6px !important;
      min-height: 50px !important;
      border-radius: 10px !important;
    }
    
    .footer-btn-premium .btn-text {
      font-size: 0.85rem !important;
    }
    
    .footer-btn-premium svg {
      font-size: 1rem !important;
      margin-right: 5px !important;
    }
  }

  /* 移动端优化 */
  @media (max-width: 768px) {
    .footer-btn-premium {
      padding: 10px 4px !important;
      min-height: 45px !important;
      border-radius: 8px !important;
      letter-spacing: 0.5px;
      margin: 3px 1px !important;
    }
    
    .footer-btn-premium .btn-text {
      font-size: 0.8rem !important;
    }
    
    .footer-btn-premium svg {
      font-size: 0.9rem !important;
      margin-right: 4px !important;
    }

    .footer-btn-premium:hover {
      transform: translateY(-2px) scale(1.01);
    }
  }

  /* 小屏手机优化 */
  @media (max-width: 480px) {
    .footer-btn-premium {
      padding: 8px 3px !important;
      min-height: 40px !important;
      border-radius: 6px !important;
      letter-spacing: 0.3px;
      margin: 2px 1px !important;
    }
    
    .footer-btn-premium .btn-text {
      font-size: 0.75rem !important;
    }
    
    .footer-btn-premium svg {
      font-size: 0.8rem !important;
      margin-right: 3px !important;
    }
  }

  /* 超小屏幕优化 */
  @media (max-width: 375px) {
    .footer-btn-premium {
      padding: 6px 2px !important;
      min-height: 35px !important;
      border-radius: 4px !important;
      letter-spacing: 0.2px;
      margin: 2px 0px !important;
    }
    
    .footer-btn-premium .btn-text {
      font-size: 0.7rem !important;
    }
    
    .footer-btn-premium svg {
      font-size: 0.75rem !important;
      margin-right: 2px !important;
    }
  }

  /* 极小屏幕优化 */
  @media (max-width: 320px) {
    .footer-btn-premium {
      padding: 5px 1px !important;
      min-height: 32px !important;
      margin: 1px 0px !important;
    }
    
    .footer-btn-premium .btn-text {
      font-size: 0.65rem !important;
      line-height: 1.1 !important;
    }
    
    .footer-btn-premium svg {
      display: none !important; /* 极小屏幕隐藏图标，只显示文字 */
    }
  }

  /* 高分辨率屏幕优化 */
  @media (min-resolution: 2dppx) {
    .footer-btn-premium {
      box-shadow: 
        0 6px 20px rgba(0, 0, 0, 0.3),
        inset 0 0.5px 0 rgba(255, 255, 255, 0.2),
        0 0 0 0.5px rgba(255, 255, 255, 0.1);
    }
  }

  /* 无障碍访问优化 */
  @media (prefers-reduced-motion: reduce) {
    .footer-btn-premium {
      transition: none;
    }
    
    .footer-btn-premium:hover {
      transform: none;
    }
    
    .footer-btn-premium::before,
    .footer-btn-premium::after {
      display: none;
    }
  }

  /* 暗色主题优化 */
  @media (prefers-color-scheme: dark) {
    .footer-btn-premium {
      box-shadow: 
        0 6px 20px rgba(0, 0, 0, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.1),
        0 0 0 1px rgba(255, 255, 255, 0.05);
    }
  }
`;

// 将样式注入到页面
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = additionalStyles;
  document.head.appendChild(styleSheet);
}

export default PartitionDetail;